import axios from "../axios";
import { entityGenderEnum, entityRoleEnum, entityVerificationStatusEnum } from "../../enums/entity.enum";
import {
  fetchEntityFollowersRequestParamsFormatter,
  fetchEntityFollowingRequestParamsFormatter,
  getEntityFollowersListRequestParamsFormatter,
  getEntityFollowersResponseFormatter,
  getEntityFollowingsListRequestParamsFormatter,
  searchEntitiesRequestParamsFormatter,
} from "../formatters/entitiesFormatter";
import { UserInfo } from "../../types";

export const updateEntity = async (entityId: number, body: any) => {
  try {
    const response = await axios.put(`/football-entities/${entityId}`, body);
    return response.data.data;
  } catch (err) {
    throw new Error(err + " Failed to update entity data");
  }
};

export const fetchFootballEntities = async () => {
  try {
    const response = await axios.get(`/football-entities/explore`);
    return response.data;
  } catch (err) {
    throw new Error(err + " Failed to fetch entities data");
  }
};

export const getEntityProfile = async (id: string) => {
  try {
    const response = await axios.get(`football-entities/${id}/`);
    return response.data;
  } catch (err) {
    throw new Error(err + " Failed to get entity profile data");
  }
};

export const getEntityOwnProfile = async () => {
  try {
    const response = await axios.get(`/users/profile/v1`);
    localStorage.setItem("entityId", response?.data?.data?.entity?.id);
    return response.data.data;
  } catch (err) {
    throw new Error(err + " Failed to fetch profile data");
  }
};

export const getEntityProfileData = async (isOwnProfile: boolean, entityId?: string): Promise<UserInfo> => {
  if (entityId && !isOwnProfile) {
    return await getEntityProfile(entityId);
  } else {
    return await getEntityOwnProfile();
  }
};

export const getFollowersCountPerAge = async (entityId: number | null) => {
  if (!entityId) return;
  try {
    const response = await axios.get(`football-entities/${entityId}/followers-count-per-age`);
    return response.data;
  } catch (err) {
    throw new Error(err + " Failed to fetch followers count per age");
  }
};

export const getEntityStatistics = async (entityId: number | null) => {
  if (!entityId) return;
  try {
    const response = await axios.get(`football-entities/${entityId}/dashboard`);
    return response.data;
  } catch (err) {
    throw new Error(err + " Failed to fetch entity statistics");
  }
};

export const getEntityFollowers = async (entityId: number | null, daysQuery: string) => {
  if (!entityId) return;

  const params = getEntityFollowersResponseFormatter(daysQuery);
  try {
    const response = await axios.get(`football-entities/${entityId}/followers-count-per-day${params}`);
    return response.data;
  } catch (err) {
    throw new Error(err + " Failed to fetch entity followers");
  }
};

export const followEntity = async (entityId: string) => {
  try {
    const response = await axios.put(`football-entities/${entityId}/follow`);
    return response.data.isFollowing;
  } catch (err) {
    throw new Error(err + " Failed to follow entity");
  }
};

export const unFollowEntity = async (entityId: string) => {
  try {
    const response = await axios.delete(`football-entities/${entityId}/follow`);
    return response.data.isFollowing;
  } catch (err) {
    throw new Error(err + " Failed to unfollow entity");
  }
};

export const registerYourEntity = async (body: any) => {
  try {
    const response = await axios.post(`/public/football-entities/contact-sales`, body);
    return response;
  } catch (err: any) {
    if (err.response.data.errors) throw new Error(err.response.data.errors);
    throw new Error(err + " Failed to register your entity");
  }
};

export const joinWaitingList = async (body: { email?: string; phoneNumber?: string }) => {
  try {
    const response = await axios.post(`/public/football-entities/join-waiting-list`, body);
    return response;
  } catch (err: any) {
    if (err.response.data.errors) throw new Error(err.response.data.errors);
    throw new Error(err + " Failed to join waiting list");
  }
};

export const fetchEntityFollowers = async (
  entityId: number,
  limit: number,
  offset: number,
  location?: string | null | "",
  country?: string | null | "",
  gender?: entityGenderEnum | string | null | "",
  verificationStatus?: entityVerificationStatusEnum | string | null | "",
  entityRole?: entityRoleEnum | string | null | "",
  nationalityId?: number | null | "",
  filters?: Record<string, any>
) => {
  try {
    const params = fetchEntityFollowersRequestParamsFormatter(
      limit,
      offset,
      location,
      country,
      gender,
      verificationStatus,
      entityRole,
      nationalityId,
      filters
    );

    const response = await axios.get(`/football-entities/${entityId}/followers${params}`);

    return response.data.data;
  } catch (err) {
    throw new Error(err + " Failed to fetch network followers");
  }
};

export const fetchEntityFollowing = async (
  entityId: number,
  limit: number,
  offset: number,
  location?: string | "" | null,
  country?: string | "" | null,
  gender?: entityGenderEnum | "" | string | null,
  verificationStatus?: entityVerificationStatusEnum | "" | string | null,
  entityRole?: entityRoleEnum | null | string | "",
  nationalityId?: number | null,
  filters?: Record<string, any> // Optional filters parameter
) => {
  try {
    const params = fetchEntityFollowingRequestParamsFormatter(
      limit,
      offset,
      location,
      country,
      gender,
      verificationStatus,
      entityRole,
      nationalityId,
      filters
    );
    const response = await axios.get(`/football-entities/${entityId}/following${params}`);

    return response.data.data;
  } catch (err) {
    throw new Error(err + " Failed to fetch network followers");
  }
};

export const searchEntities = async (
  limit: number,
  offset: number,
  searchValue?: string,
  countryId?: string | "",
  nationalityId?: string | "",
  isManagedByEntity?: boolean,
  trending?: boolean,
  verificationStatus?: "VERIFIED" | ""
) => {
  try {
    const params = searchEntitiesRequestParamsFormatter(
      limit,
      offset,
      searchValue,
      countryId,
      nationalityId,
      isManagedByEntity,
      trending,
      verificationStatus
    );
    const response = await axios.get(`/search/football-entities${params}`);

    return response.data;
  } catch (err) {
    throw new Error(err + " Failed to fetch user followers");
  }
};

export const getTrendingEntities = async () => {
  try {
    const response = await searchEntities(3, 0, "", "", "", false, true);
    return response.items;
  } catch (err) {
    throw new Error(err + " Failed to get trending professionals");
  }
};

export const talkToSales = async (body: any) => {
  try {
    return await axios.post(`/public/football-entities/contact-sales`, body);
  } catch (err) {
    console.error(err + " Failed to contact sales");
    return false;
  }
};

export const visitEntity = async (entityId: string): Promise<any> => {
  try {
    const response = await axios.put<any>(`/football-entities/${entityId}/visited`);
    return response.data;
  } catch (err) {
    throw new Error(err + " Failed visit entity profile");
  }
};

export const getAudienceByLocation = async (
  footballEntityId: number | null,
  locationType: "CITY" | "COUNTRY"
): Promise<any> => {
  if (!footballEntityId) return;
  try {
    const response = await axios.get<any>(
      `/football-entities/${footballEntityId}/audience-by-location?locationType=${locationType}`
    );
    return response.data;
  } catch (err) {
    throw new Error(err + " Failed to get audience by location");
  }
};

export const getInteractions = async (footballEntityId: number | null, period: string): Promise<any> => {
  if (!footballEntityId) return;
  const QUERY = period === "ALL_TIME" ? "" : `?period=${period.toLocaleLowerCase()}`;

  try {
    const response = await axios.get<any>(`/football-entities/${footballEntityId}/interactions${QUERY}`);
    return response.data;
  } catch (err) {
    throw new Error(err + " Failed to get followers per age");
  }
};

export const getUsersToChatWith = async (footballEntityId: number | null, searchValue: string): Promise<any> => {
  if (!footballEntityId) return;
  try {
    const response = await axios.get<any>(
      `/football-entities/${footballEntityId}/users-to-chat-with?limit=30&search=${searchValue}`
    );
    return response.data.data;
  } catch (err) {
    throw new Error(err + " Failed to get users to chat with");
  }
};

export const getEntityFollowersList = async (entityId: number | null, limit: number, offset: number) => {
  if (!entityId) return;

  const params = getEntityFollowersListRequestParamsFormatter(limit, offset);
  try {
    const response = await axios.get(`football-entities/${entityId}/followers${params}`);
    return response.data.data;
  } catch (err) {
    throw new Error(err + " Failed to fetch entity followers list");
  }
};

export const getEntityFollowingList = async (entityId: number | null, limit: number, offset: number) => {
  if (!entityId) return;

  const params = getEntityFollowingsListRequestParamsFormatter(limit, offset);
  try {
    const response = await axios.get(`football-entities/${entityId}/followers${params}`);
    return response.data.data;
  } catch (err) {
    throw new Error(err + " Failed to fetch entity followings list");
  }
};
